import React, { useState, useEffect } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { setPageTitle } from "../../utils/pageTitle";
import {
  ClipboardList,
  ChevronLeft,
  Home,
  Settings,
  Shield,
  Menu,
  X,
  BarChart3,
  Users,
  Mail,
  Target,
  Calculator,
  Search,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { employeeNamesService } from "../../services/employeeNames";
import { UserInfoSection } from "../layout/UserInfoSection";
import "../../styles/ppc-farm-override.css";

const PpcFarmLayout = () => {
  const [displayName, setDisplayName] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = "PPC Farm Mail Tracker";

    if (path === "/ppc-farm" || path === "/ppc-farm/dashboard") {
      title = "PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/keyword-discovery") {
      title = "Keyword Discovery | PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/target-ledger") {
      title = "Target Ledger | PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/cohort-ledger") {
      title = "Cohort Ledger | PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/mailing-ledger") {
      title = "Mailing Ledger | PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/cohort-builder") {
      title = "Cohort Builder | PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/potential-problems") {
      title = "Potential Problems | PPC Farm Mail Tracker";
    } else if (path.startsWith("/ppc-farm/review-session")) {
      title = "Review Session | PPC Farm Mail Tracker";
    } else if (path === "/ppc-farm/mailing-cost-dashboard") {
      title = "Mailing Cost Dashboard | PPC Farm Mail Tracker";
    } else if (path.startsWith("/ppc-farm/settings")) {
      title = "Settings | PPC Farm Mail Tracker";
    }

    setPageTitle(title);
  }, [location.pathname]);

  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const { isAdmin } = useIsAdmin();

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden ppc-farm-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label="Toggle navigation menu"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
        <h1 className="ml-4 text-lg font-semibold text-gray-900 dark:text-white">
          PPC Farm Mail Tracker
        </h1>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0 hidden md:flex"}
          ${isCollapsed && !isMobileMenuOpen ? "w-16 md:w-16" : "w-64 md:w-64"}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block z-50"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft
            className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`}
          />
        </button>

        <div
          className="border-b flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate("/ppc-farm/dashboard")}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <ClipboardList className="h-12 w-12 text-yellow-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <ClipboardList className="h-20 w-20 text-yellow-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                PPC Farm Mail Tracker
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage Direct Mail Campaigns
              </p>
            </div>
          )}
        </div>

        <nav
          className="flex-1 mt-4 overflow-y-auto flex flex-col"
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Main Navigation */}
          <div
            onClick={() => handleNavigate("/ppc-farm/dashboard")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/dashboard")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/dashboard");
              }
            }}
          >
            <BarChart3
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Dashboard"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/cohort-builder")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/cohort-builder")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/cohort-builder");
              }
            }}
          >
            <Target
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Cohort Builder"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/keyword-discovery")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/keyword-discovery")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/keyword-discovery");
              }
            }}
          >
            <Search
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Keyword Discovery"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/potential-problems")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/potential-problems")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/potential-problems");
              }
            }}
          >
            <AlertTriangle
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Potential Problems"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/mailing-cost-dashboard")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/mailing-cost-dashboard")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/mailing-cost-dashboard");
              }
            }}
          >
            <Calculator
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Mailing Cost Dashboard"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/cohort-ledger")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/cohort-ledger")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/cohort-ledger");
              }
            }}
          >
            <Users
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Cohort Ledger"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/mailing-ledger")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/mailing-ledger")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/mailing-ledger");
              }
            }}
          >
            <Mail
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Mailing Ledger"}
          </div>

          <div
            onClick={() => handleNavigate("/ppc-farm/target-ledger")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/target-ledger")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/target-ledger");
              }
            }}
          >
            <ClipboardList
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Target Ledger"}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={() => handleNavigate("/ppc-farm/settings")}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive("/ppc-farm/settings")
                ? "bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-300"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/ppc-farm/settings");
              }
            }}
          >
            <Settings
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Settings"}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate("/admin")}
              className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleNavigate("/admin");
                }
              }}
            >
              <Shield
                className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
              />
              {(!isCollapsed || isMobileMenuOpen) && "Admin"}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate("/dashboard")}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNavigate("/dashboard");
              }
            }}
          >
            <Home
              className={`${isCollapsed && !isMobileMenuOpen ? "" : "mr-3"} h-5 w-5`}
            />
            {(!isCollapsed || isMobileMenuOpen) && "Apps Hub"}
          </div>
        </nav>

        <UserInfoSection
          displayName={displayName}
          isCollapsed={isCollapsed && !isMobileMenuOpen}
        />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet />
      </main>
    </div>
  );
};

export default PpcFarmLayout;
